import {
  addWishlistItemTagAction,
  addWishlistTagAction,
} from "@/actions/wishlist";
import { useMarket } from "@app/_context";
import useAuth from "@features/auth/hooks/useAuth";
import type { WishlistItem } from "@repo/core/wishlist";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import invalidateWishlistQueries from "../utils/invalidateWishlistQueries";

export default function useAddWishlistTag() {
  const { market } = useMarket().state;
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: async ({
      name,
      item,
    }: {
      name: string;
      item?: WishlistItem;
    }) => {
      if (!token) return;

      const tag = await addWishlistTagAction({
        token,
        name,
        locale: market.serviceLocale,
      });

      if (item?.productId && tag.id) {
        await addWishlistItemTagAction({
          token,
          locale: market.serviceLocale,
          payload: {
            productId: item.productId,
            variantId: item.variantId,
            tagId: tag.id,
          },
        });
      }

      return tag;
    },
    onSettled: () => invalidateWishlistQueries(queryClient),
  });

  const addWishlistTag = useCallback(
    (name: string, item?: WishlistItem) => {
      mutate({ name, item });
    },
    [mutate],
  );

  const addWishlistTagAsync = useCallback(
    (name: string, item?: WishlistItem) => {
      return mutateAsync({ name, item });
    },
    [mutateAsync],
  );

  return {
    addWishlistTag,
    addWishlistTagAsync,
    isPending,
    isSuccess,
  };
}
